
import { CheckIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { ComponentPropsWithoutRef,ComponentType } from 'react';

import { cn } from '@/services/utils';

import { Badge } from '../core/Badge';
import { Button } from '../core/Button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '../core/Command';
import { Icon } from '../core/Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../core/Popover';

interface FacetedFilterOption {
    icon?: ComponentType<ComponentPropsWithoutRef<'svg'>> | string;
    label: string;
    value: string;
}

interface FacetedFilterAction {
    type: 'added' | 'removed';
    option: FacetedFilterOption;
}

interface FacetedFilterActionWithoutPayload {
    type: 'removed_all';
}

type FacetedFilterActionDispatcher = React.Dispatch<FacetedFilterAction | FacetedFilterActionWithoutPayload>;

interface FacetedFilterProps {
    title: string;
    options: FacetedFilterOption[];
    selectedOptions: FacetedFilterOption[];
    dispatch: FacetedFilterActionDispatcher
}

const FacetedFilter = ({ title, options, selectedOptions, dispatch } : FacetedFilterProps) => {
    const selectedValues = new Set(selectedOptions.map(option => option.value ));

    if (!selectedValues) return null;

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline">
                    <span className="tw-mr-1">
                        <Icon icon={FunnelIcon} variant="secondary" size="sm" />
                    </span>
                    {title}
                    {selectedValues?.size > 0 && (
                        <div className="tw-inline-flex">
                            <span className="tw-mx-1">|</span>
                            <div className="tw-hidden tw-space-x-1 lg:tw-flex">
                                {selectedValues.size > 2 ? (
                                    <Badge>
                                        {selectedValues.size} selected
                                    </Badge>
                                ) : (
                                    options
                                        .filter((option) => selectedValues.has(option.value))
                                        .map((option) => (
                                            <Badge
                                                key={option.value}>
                                                {option.label}
                                            </Badge>
                                        ))
                                )}
                            </div>
                        </div>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="tw-w-[200px] tw-p-0" align="start">
                <Command>
                    <CommandInput placeholder={title} />
                    <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => {
                                const isSelected = selectedValues.has(option.value);
                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => {
                                            if (isSelected) {
                                                dispatch({
                                                    type: 'removed',
                                                    option: option
                                                });
                                            } else {
                                                dispatch({
                                                    type: 'added',
                                                    option: option
                                                });
                                            }
                                        }}>
                                        <div
                                            className={cn(
                                                'tw-mr-2 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-primary',
                                                isSelected
                                                    ? 'tw-bg-primary tw-text-primary-foreground'
                                                    : 'tw-opacity-50 [&_svg]:tw-invisible'
                                            )}>
                                            <Icon icon={CheckIcon} variant="light" size="sm" />
                                        </div>
                                        {option.icon && (
                                            <Icon icon={option.icon} size="sm" />
                                        )}
                                        <span>{option.label}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {selectedValues.size > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={() => dispatch({ type: 'removed_all' })}
                                        className="tw-justify-center tw-text-center">
                                        Clear filters
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

const filtersReducer = (options: FacetedFilterOption[], action: FacetedFilterAction | FacetedFilterActionWithoutPayload) => {
    switch (action.type) {
        case 'added': {
            return [
                ...options,
                action.option,
            ];
        }
        case 'removed': {
            return options.filter((option) => option.value !== action.option?.value);
        }
        case 'removed_all': {
            return [];
        }
        default: {
            return [];
        }
    }
};

export {
    FacetedFilter,
    type FacetedFilterActionDispatcher,
    type FacetedFilterOption,
    filtersReducer
};
